import { Component, inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { AgentAccessService, AgentAccessToken } from '../../../services/agent-access.service';

export type AgentClientKind = 'claude-code' | 'claude-desktop' | 'cursor';

/** Setup text for one client — URL and token filled in. Exported for the spec. */
export function agentSetupSnippet(kind: AgentClientKind, url: string, token: string): string {
  switch (kind) {
    case 'claude-code':
      return `claude mcp add --transport http alphalearn ${url} --header "Authorization: Bearer ${token}"`;
    case 'cursor':
      return JSON.stringify(
        { mcpServers: { alphalearn: { url, headers: { Authorization: `Bearer ${token}` } } } },
        null,
        2,
      );
    case 'claude-desktop':
      // Claude Desktop connects remote servers with OAuth only; until we offer that, mcp-remote
      // bridges with the header (passed via env so the space in "Bearer …" survives).
      return JSON.stringify(
        {
          mcpServers: {
            alphalearn: {
              command: 'npx',
              args: ['-y', 'mcp-remote', url, '--header', 'Authorization:${AUTH_HEADER}'],
              env: { AUTH_HEADER: `Bearer ${token}` },
            },
          },
        },
        null,
        2,
      );
  }
}

/**
 * Make a personal agent token, then show it ONCE with the setup for Claude
 * Code, Claude Desktop and Cursor. Closing returns the created token (without
 * the plain value) so the list can update.
 */
@Component({
  selector: 'app-agent-token-dialog',
  templateUrl: './agent-token-dialog.component.html',
  styleUrls: ['./agent-token-dialog.component.scss'],
})
export class AgentTokenDialogComponent {
  private readonly fb = inject(FormBuilder);
  private readonly access = inject(AgentAccessService);
  private readonly toastr = inject(NbToastrService);
  private readonly translate = inject(TranslateService);
  readonly dialogRef = inject(NbDialogRef<AgentTokenDialogComponent>);

  readonly lifetimes = [30, 90, 365];
  readonly clients: AgentClientKind[] = ['claude-code', 'claude-desktop', 'cursor'];
  readonly mcpUrl = this.access.mcpUrl;

  readonly form = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(80)]],
    expires_in_days: [90],
    can_write: [true],
  });

  saving = false;
  created: AgentAccessToken | null = null;
  activeClient: AgentClientKind = 'claude-code';

  submit(): void {
    if (this.saving || this.created) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.access
      .create(this.form.getRawValue())
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (token) => (this.created = token),
        error: (err) =>
          this.toastr.danger(
            this.translate.instant(
              err?.error?.detail === 'too_many_tokens'
                ? 'pages.settings.agentAccess.errors.tooMany'
                : 'pages.settings.agentAccess.errors.create',
            ),
            this.translate.instant('toasts.error'),
          ),
      });
  }

  snippet(kind: AgentClientKind): string {
    return agentSetupSnippet(kind, this.mcpUrl, this.created?.token ?? '');
  }

  async copy(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      this.toastr.success(this.translate.instant('pages.settings.agentAccess.copied'), '');
    } catch {
      this.toastr.warning(this.translate.instant('pages.settings.agentAccess.copyFailed'), '');
    }
  }

  close(): void {
    const { token: _plain, ...rest } = this.created ?? ({} as AgentAccessToken);
    this.dialogRef.close(this.created ? rest : null);
  }
}
